import React, {useState} from 'react'
import _ from 'lodash'
import classNames from 'classnames'
import dayjs from 'dayjs'
import {Formik} from 'formik'
import {Button, Input, message, Modal} from 'antd'
import {LoadingOutlined} from '@ant-design/icons'
import {FaPlus} from 'react-icons/fa6'
import {MdChurch} from 'react-icons/md'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import DrawDialog from 'components/drawer/dialog/DrawDialog'
import LookupField from 'components/LookupField'
import {Field, Section} from 'components/form/ModernForm'
import {renderParishLookupOption} from '../functions/lookupOptionRenderers'
import {
    familyBook_addFamilyBookTransferLog_Api,
    familyBook_deleteFamilyBookTransferLog_Api,
    familyBook_getFamilyBookTransferLogs_Api,
    familyBook_updateFamilyBookTransferLog_Api,
} from 'apis/familyBook'

// 1 dòng lịch sử chuyển giáo xứ trong list - cùng tinh thần VocationRow (MemberDetailModal.js):
// dòng ĐANG mở (end_date rỗng) là dòng "Hiện tại" - badge nổi bật, còn lại hiện khoảng ngày đã đóng.
const TransferLogRow = ({entry, onClick, t}) => (
    <div
        onClick={onClick}
        className={classNames(
            'p-2.5 flex items-center gap-2 rounded-lg border transition-all',
            entry.end_date ? 'border-color-100 bg-color-50' : 'border-family-200 bg-family-50',
            onClick && 'cursor-pointer hover:shadow-md'
        )}>
        <MdChurch size={16} className={classNames('flex-shrink-0', entry.end_date ? 'text-color-400' : 'text-family-500')}/>
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-color-000 leading-snug truncate">{entry.parish_name}</span>
                {!entry.end_date && (
                    <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-4xs font-bold uppercase tracking-wide bg-family-500 text-white">
                        {t('transfer log current badge')}
                    </span>
                )}
            </div>
            <div className="text-2xs text-color-500 mt-0.5">
                {entry.eff_date ? dayjs(entry.eff_date).format('DD/MM/YYYY') : '?'}
                {' – '}
                {entry.end_date ? dayjs(entry.end_date).format('DD/MM/YYYY') : t('transfer log current badge')}
            </div>
            {entry.note && <div className="text-2xs text-color-400 italic mt-0.5">{entry.note}</div>}
        </div>
    </div>
)

// Thêm mới/sửa 1 dòng lịch sử chuyển giáo xứ - CÙNG convention VocationEditForm: API riêng theo id
// (add khi `entry` rỗng, update khi đã có entry.id), xoá đặt trong footer form. KHÁC vocation ở chỗ
// luôn hiện cả eff_date lẫn end_date NGAY LÚC THÊM MỚI (không chỉ lúc sửa) - mục đích chính của
// "thêm tay" ở đây là BỔ SUNG 1 giai đoạn ĐÃ QUA (đã biết trước cả ngày bắt đầu lẫn kết thúc), khác
// vocation-add vốn mặc định "vừa mới bắt đầu, còn đang hoạt động".
const TransferLogEditForm = ({familyBookId, entry, onClose, onSuccess, onDeleted, t}) => {
    const isNew = !entry?.id

    const {handleAsyncAction: doCreate, loading: creating} = useAsyncAction({
        apiInfo: familyBook_addFamilyBookTransferLog_Api,
        onSuccess: () => {
            message.success(t('transfer log updated'))
            onSuccess && onSuccess()
            onClose && onClose()
        },
        onError: () => message.error(t('error occurred')),
    })
    const {handleAsyncAction: doUpdate, loading: updating} = useAsyncAction({
        apiInfo: familyBook_updateFamilyBookTransferLog_Api,
        onSuccess: () => {
            message.success(t('transfer log updated'))
            onSuccess && onSuccess()
            onClose && onClose()
        },
        onError: () => message.error(t('error occurred')),
    })
    const {handleAsyncAction: doDelete, loading: deleting} = useAsyncAction({
        apiInfo: familyBook_deleteFamilyBookTransferLog_Api,
        onSuccess: () => {
            message.success(t('transfer log deleted'))
            onDeleted ? onDeleted() : onSuccess && onSuccess()
            onClose && onClose()
        },
        onError: () => message.error(t('error occurred')),
    })

    const initialValues = {
        parish: entry?.parish ? {id: entry.parish.id, value: entry.parish.name || entry.parish_name} : null,
        parish_name: entry?.parish_name || '',
        eff_date: entry?.eff_date ? dayjs(entry.eff_date).format('YYYY-MM-DD') : '',
        end_date: entry?.end_date ? dayjs(entry.end_date).format('YYYY-MM-DD') : '',
        source_idcode: entry?.source_idcode || '',
        note: entry?.note || '',
    }

    const confirmDelete = () => {
        Modal.confirm({
            rootClassName: 'family-theme',
            title: t('confirm delete transfer log'),
            content: entry.parish_name,
            okText: t('delete'),
            okButtonProps: {danger: true},
            cancelText: t('cancel'),
            onOk: () => doDelete({}, {id: entry.id}),
        })
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => {
                const payload = {
                    parish_id: values.parish?.id || undefined,
                    parish_name: values.parish?.value || values.parish_name || undefined,
                    eff_date: values.eff_date || undefined,
                    // "" (rỗng) khác undefined (bỏ qua field) - cho phép XOÁ end_date đã set nhầm,
                    // cùng convention VocationEditForm.
                    end_date: values.end_date || '',
                    source_idcode: values.source_idcode || undefined,
                    note: values.note || undefined,
                }
                if (isNew) {
                    doCreate(payload, {id: familyBookId})
                } else {
                    doUpdate(payload, {id: entry.id})
                }
            }}>
            {({values, setFieldValue, setValues, handleSubmit, isSubmitting}) => (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="font-bold text-lg text-color-000 uppercase tracking-wide text-center">
                        {t('transfer log title')}
                    </div>

                    <Section title={t('parish')} subtitle={t('required')}>
                        <LookupField
                            title={t('choose parish')}
                            createIfNotExists={true}
                            defaultValue={values.parish_name}
                            selectedOption={values.parish}
                            apiPath="/lookup/parish/get-by-types"
                            name="transfer_log_parish"
                            renderOption={renderParishLookupOption}
                            handleChange={(e) => setValues({...values, parish: e.target, parish_name: e.target.value})}
                        />
                    </Section>

                    {/* native <input type="date"> - đúng convention đã chốt cho phân hệ Sổ Gia
                        Đình, xem SacramentEditForm/VocationEditForm. */}
                    <div className={classNames('grid gap-2 grid-cols-2')}>
                        <Field label={t('transfer log from date')}>
                            <Input
                                type="date"
                                size="large"
                                className="w-full"
                                value={values.eff_date}
                                onChange={(e) => setFieldValue('eff_date', e.target.value)}
                            />
                        </Field>
                        <Field label={t('transfer log to date')}>
                            <Input
                                type="date"
                                size="large"
                                className="w-full"
                                value={values.end_date}
                                onChange={(e) => setFieldValue('end_date', e.target.value)}
                            />
                        </Field>
                    </div>
                    <span className="text-2xs text-color-300 -mt-3">{t('transfer log to date hint')}</span>

                    <Section title={t('family book source code')} subtitle={t('optional')}>
                        <Input
                            size="large"
                            value={values.source_idcode}
                            onChange={(e) => setFieldValue('source_idcode', e.target.value)}
                        />
                    </Section>

                    <Section title={t('transfer log note')} subtitle={t('optional')}>
                        <Input.TextArea
                            rows={2}
                            value={values.note}
                            onChange={(e) => setFieldValue('note', e.target.value)}
                            placeholder={t('transfer log note placeholder')}
                        />
                    </Section>

                    <div className="flex items-center justify-between gap-2 pt-4 mt-1 border-t border-color-50">
                        {!isNew ? (
                            <span
                                onClick={deleting ? undefined : confirmDelete}
                                className="font-semibold text-sm text-red-500 hover:text-red-600 cursor-pointer">
                                {t('delete')}
                            </span>
                        ) : <span/>}
                        <div className="flex items-center gap-2">
                            <span
                                onClick={isSubmitting ? undefined : onClose}
                                className="font-semibold text-sm text-color-300 hover:text-color-000 cursor-pointer">
                                {t('cancel')}
                            </span>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={isSubmitting || creating || updating}
                                className="font-semibold button-rounded-lg">
                                {t('save')}
                            </Button>
                        </div>
                    </div>
                </form>
            )}
        </Formik>
    )
}

// Danh sách Lịch Sử Chuyển Sổ - hiện BÊN DƯỚI LookupField "Thuộc giáo xứ" (giá trị HIỆN TẠI/master,
// đổi qua chính form Sửa Sổ như cũ) trong EditFamilyBookModal.js. Tự fetch riêng qua
// familyBook_getFamilyBookTransferLogs_Api (refreshCount cục bộ) - KHÔNG dùng chung onSuccess của
// form Sửa Sổ (submit form đó đóng cả modal, xem FamilyBooks.js/FamilyBookDetail.js), cùng pattern
// FamilyBookAdminsSection ở EditFamilyBookModal.js.
const FamilyBookTransferLogSection = ({familyBookId, canEdit, t}) => {
    const [refreshCount, setRefreshCount] = useState(0)

    const {response, isLoading} = useAsync({
        apiInfo: familyBook_getFamilyBookTransferLogs_Api,
        query: {id: familyBookId},
        deps: [familyBookId, refreshCount],
    })
    const histories = _.get(response, 'data.data', _.get(response, 'data', []))

    return (
        <div className="flex flex-col gap-2 mt-2">
            <div className="flex items-center justify-between gap-2">
                <span className="text-2xs font-bold uppercase tracking-widest text-color-300">{t('transfer log title')}</span>
                {canEdit !== false && (
                    <DrawDialog
                        width={420}
                        wrapClassName="family-theme"
                        component={
                            <button
                                type="button"
                                title={t('add transfer log')}
                                className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-lg bg-family-100 text-family-700 hover:bg-family-200 cursor-pointer">
                                <FaPlus size={10}/>
                            </button>
                        }
                        footer={false}>
                        {(isToggle, toggle) => isToggle && (
                            <TransferLogEditForm
                                familyBookId={familyBookId}
                                entry={null}
                                onClose={() => toggle(false)}
                                onSuccess={() => setRefreshCount((c) => c + 1)}
                                t={t}
                            />
                        )}
                    </DrawDialog>
                )}
            </div>
            <span className="text-2xs text-color-400">{t('transfer log hint')}</span>

            {isLoading && !histories.length && (
                <div className="flex items-center justify-center py-3"><LoadingOutlined className="text-family-500"/></div>
            )}
            {!isLoading && !histories.length && (
                <span className="text-xs italic text-color-300">{t('transfer log not recorded')}</span>
            )}
            <div className="flex flex-col gap-2">
                {histories.map((entry) => {
                    if (canEdit === false) {
                        return <TransferLogRow key={entry.id} entry={entry} t={t}/>
                    }
                    return (
                        <DrawDialog
                            key={entry.id}
                            width={420}
                            wrapClassName="family-theme"
                            component={<TransferLogRow entry={entry} t={t}/>}
                            footer={false}>
                            {(isToggle, toggle) => isToggle && (
                                <TransferLogEditForm
                                    familyBookId={familyBookId}
                                    entry={entry}
                                    onClose={() => toggle(false)}
                                    onSuccess={() => setRefreshCount((c) => c + 1)}
                                    t={t}
                                />
                            )}
                        </DrawDialog>
                    )
                })}
            </div>
        </div>
    )
}

export default FamilyBookTransferLogSection
